import { AxiosError } from 'axios'
import { action, computed, makeObservable, observable, override } from 'mobx'
import { task } from 'mobx-task'
import { combineLatest, distinctUntilChanged, Subscription } from 'rxjs'
import { func, validationContext } from 'validx'
import { AlertFactory, AlertState } from 'alerts/Alert'
import { SearchableUser } from 'iam/models/SearchableUser'
import Skill from 'iam/models/Skill'
import Workspace from 'iam/models/Workspace'
import MemberStore from 'iam/MemberStore'
import SkillStore from 'iam/SkillStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import ModalViewModel from 'infra/ModalViewModel'
import { Id, Nullable } from 'types/definitions'
import { tapAction, toStream } from 'utils/mobx-util'
import { pluralize } from 'utils/pluralization-util'

interface SkillManagementResult {
  user: SearchableUser
  success?: boolean
  error?: AxiosError
}

export enum ManageSkillsDialogTab {
  Add = 'Add',
  Remove = 'Remove',
}

export class ManageSkillsViewModel extends ModalViewModel {
  /**
   * Subscription to stream of changes to observables.
   */
  private subscription = new Subscription()

  /**
   * The currently selected tab.
   */
  @observable
  selectedTab: ManageSkillsDialogTab = ManageSkillsDialogTab.Add

  /**
   * Whether to use the passed-in user as the dialog target.
   */
  @observable
  selectedUser: Nullable<SearchableUser> = null

  /**
   * The list of available skills.
   */
  @observable
  availableSkills: Skill[] = []

  /**
   * The currently selected workspace.
   */
  @observable
  workspace: Nullable<Workspace> = null

  /**
   * The currently selected skills.
   */
  @observable
  skills: Skill[] = []

  /**
   * Validation rules for managing skills.
   */
  validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    skills: [
      func(() => this.skills.length > 0, 'You must select at least one skill.'),
    ],
  })

  /**
   * Stream of changes to the skill form inputs.
   */
  private skillSelections$ = combineLatest([
    toStream(() => this.workspace, false),
    toStream(() => this.selectedTab, false),
  ]).pipe(
    distinctUntilChanged((prev, curr) => {
      return prev[0]?.id === curr[0]?.id && prev[1] === curr[1]
    }),
    tapAction(([workspace]) => {
      if (workspace) {
        this.clearSelections()
        this.updateOptions()
      }
    }),
  )

  constructor(
    private _selectedWorkspace: () => Workspace,
    private selectedUserIds: () => Set<Id>,
    private users: () => SearchableUser[],
    private toggleUser: (checked: boolean, userId: Id) => void,
    private memberStore: MemberStore,
    private skillStore: SkillStore,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
  }

  /**
   * Activate the view model.
   */
  activate() {
    this.subscription = this.skillSelections$.subscribe()
  }

  /**
   * Deactivate the view model.
   */
  deactivate() {
    this.subscription.unsubscribe()
  }

  /**
   * The currently selected workspace.
   */
  @computed
  get selectedWorkspace(): Nullable<Workspace> {
    return this._selectedWorkspace() === Workspace.none
      ? null
      : this._selectedWorkspace()
  }

  /**
   * The currently selected users.
   */
  @computed
  get selectedUsers() {
    return this.users().filter((u) => this.selectedUserIds().has(u.publicId))
  }

  /**
   * The list of workspaces available to the user.
   */
  @computed
  get workspaces() {
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.hasPermission('ADMIN_USER_LIST'))
      .map((mb) => mb.workspace)
  }

  /**
   * Untoggle a user.
   */
  @action.bound
  untoggleUser(userId: Id) {
    this.toggleUser(false, userId)
  }

  /**
   * Set the selected tab.
   */
  @action.bound
  setSelectedTab(tab: ManageSkillsDialogTab) {
    this.selectedTab = tab
  }

  /**
   * Set the selected user.
   */
  @action.bound
  setSelectedUser(user: Nullable<SearchableUser>) {
    this.selectedUser = user
  }

  /**
   * Set the list of available skills.
   */
  @action.bound
  setAvailableSkills(skills: Skill[]) {
    this.availableSkills = skills
  }

  /**
   * Set the workspace field.
   */
  @action.bound
  setWorkspace(workspace: Nullable<Workspace>) {
    this.workspace = workspace
  }

  /**
   * Set the skills field.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = skills
  }

  /**
   * Update the input options if a workspace is changed.
   */
  @action.bound
  async updateOptions() {
    if (!this.workspace) {
      return
    }

    await this.skillStore.find({ workspace_id: Id.assert(this.workspace.id) })

    this.setAvailableSkills(
      this.skillStore.forWorkspace(Id.assert(this.workspace.id)),
    )
  }

  /**
   * Clears selections when the workspace is changed.
   */
  @action.bound
  clearSelections() {
    this.setSkills([])
  }

  /**
   * Initializes the input options.
   */
  @action.bound
  async initialize() {
    if (this.selectedWorkspace !== null) {
      this.setWorkspace(this.selectedWorkspace)
    }
    this.updateOptions()
  }

  /**
   * Opens the dialog with the contextual user.
   */
  @action.bound
  showWithUser(user: SearchableUser) {
    this.initialize()
    this.setSelectedUser(user)
    super.show()
  }

  /**
   * Opens the dialog with all the selected users.
   */
  @action.bound
  showWithSelections() {
    if (this.selectedUsers.length < 1) {
      this.newAlert()
        .withState(AlertState.Neutral)
        .saying(
          'No users selected.',
          'You must select at least one user for whom to manage skills.',
        )
        .autoDismiss(2500)
        .show()
      return
    }
    this.initialize()
    super.show()
  }

  /**
   * Clears manage skills dialog state.
   */
  @action.bound
  reset() {
    this.setSelectedTab(ManageSkillsDialogTab.Add)
    this.setSelectedUser(null)
    this.setAvailableSkills([])
    this.setWorkspace(null)
    this.setSkills([])
  }

  /**
   * Update the selected user(s) by adding/removing the selected skills.
   */
  @action.bound
  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const users = this.selectedUser ? [this.selectedUser] : this.selectedUsers
    const operation = (
      workspace: Workspace,
      userPublicId: Id,
      skills: Skill[],
    ) =>
      this.selectedTab === ManageSkillsDialogTab.Add
        ? this.memberStore.addManySkillsByPublicId(
            workspace,
            userPublicId,
            skills,
          )
        : this.memberStore.removeManySkillsByPublicId(
            workspace,
            userPublicId,
            skills,
          )

    const results: SkillManagementResult[] = await Promise.all(
      users.map((user) =>
        operation(this.workspace!, user.publicId, this.skills)
          .then(() => {
            return { user, success: true }
          })
          .catch((error: AxiosError) => {
            return { user, error }
          }),
      ),
    )

    const successful = results.filter((r) => r.success)
    const failed = results.filter((r) => r.error)

    // If all skill updating succeeded, close dialog.
    if (failed.length < 1) {
      this.newAlert()
        .withState(AlertState.Success)
        .saying(
          'Success!',
          `Skills updated for ${successful.length} ${pluralize('user', successful.length)}.`,
        )
        .autoDismiss(2500)
        .show()
      this.close()
      return
    }

    // If any skill updating failed, deselect successfully updated users.
    this.newAlert()
      .withState(AlertState.Error)
      .saying(
        `Failed to update skills for ${failed.length} ${pluralize('user', failed.length)}.`,
      )
      .autoDismiss(2500)
      .show()
    successful.forEach((r) => this.untoggleUser(r.user.publicId))
  })

  /**
   * Close the dialog and reset selections to default.
   */
  @override
  close() {
    this.reset()
    super.close()
  }
}
